import classNames from "classnames";
import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Popover, PopoverBody } from "Components/ReactstrapSlim";
import { useClickOutside } from "Hooks";
import ReviewList from "./ReviewList";
import { useDispatch, useSelector } from "react-redux";
import * as Popper from "popper.js";

interface ReviewPopupOptions {
   target: HTMLElement,
   workspaceId: Guid
}

export interface ReviewPopupHandle {
   open: (options: ReviewPopupOptions) => void,
}

interface ReviewPopupProps {
   className?: string;
}

type ExntededPopperData = Popper.Data & {
   storedTop: number
}

const ReviewPopup = React.forwardRef<ReviewPopupHandle, ReviewPopupProps>(({ className }, ref) => {
   const reviewPopupStoreOptions = useSelector(state => state.reviewPopup.options);
   const dispatch = useDispatch<typeof LSStore.instance.dispatch>();

   const [popupOption, setPopupOptions] = useState<ReviewPopupOptions | undefined>(undefined);
   const [workspaceUrl, setWorkspaceUrl] = useState<string | undefined>(undefined);
   const popoverRef = useRef<HTMLDivElement>(null);

   const closePopup = () => {
      setPopupOptions(undefined);
      dispatch(LSStore.reviewPopupSlice.actions.close());
   }

   useClickOutside(popoverRef, (event: Event) => {            
      closePopup();
   });

   useImperativeHandle(ref, () => ({
      open: setPopupOptions
   }));

   useEffect(() => {
      if (popupOption == undefined && reviewPopupStoreOptions != undefined)
         setPopupOptions({
            target: reviewPopupStoreOptions.target as HTMLElement,
            workspaceId: reviewPopupStoreOptions.workspaceId
         })
      else {
         setPopupOptions(undefined);
         if (reviewPopupStoreOptions != undefined) {
            setTimeout(() => setPopupOptions({
               target: reviewPopupStoreOptions.target as HTMLElement,
               workspaceId: reviewPopupStoreOptions.workspaceId
            }), 10);
         }
      }
   }, [reviewPopupStoreOptions])   

   useEffect(() => {
      if(popupOption == undefined) {
         setWorkspaceUrl(undefined);
      }
   }, [popupOption]);

   return popupOption == undefined
      ? null
      : (<Popover fade={false} placement={"top"} isOpen={true} target={popupOption.target} toggle={() => { closePopup(); }} className={classNames("review-popup", className)} modifiers={
         {
            /*
             * Save/Restore Y position solves the problem of preventOverflow modifier in popper v1:
             * it always tries to fit in the boundry on both primary (left/right - for placement="top") and secondary (top/bottom - for placement="top") axis.
             * To skip secondary axis preventOverflow, we have to store the top value before preventOverflow and restore it after.
             * In popper v2 it is simply done by setting altAxes to false in preventOverflow modifier options
             * 
             * Additionally added a closeOnHide modifier, that closes the popover when target is considered hidden.
             * It also not working as good as it could, because in v1 detection of "isVisible" isn't correct.
             * It would've been better with Intersection Observer
            */
            saveYPosition: {
               enabled: true,
               fn(data, options) {
                  (data as ExntededPopperData).storedTop = data.offsets.popper.top;

                  return data;
               },
               order: 250
            },            
            restoreYPosition: {
               enabled: true,
               fn(data, options) {
                  data.offsets.popper.top = (data as ExntededPopperData).storedTop;

                  return data;
               },
               order: 350
            },            
            closeOnHide: {
               enabled: true,
               fn: (data, options) => {
                  if (data.hide) {
                     if (data.instance.popper.parentNode != null)
                        data.instance.popper.parentNode.removeChild(data.instance.popper);
                  }
                  return data;
               }
            }
         }
      }>
         {({ scheduleUpdate }) =>
         (<div ref={popoverRef}>
            <PopoverBody>
               <ReviewList className="review-list-compact" workspaceId={popupOption.workspaceId} take={2} onLoaded={(reviews) => {
                  scheduleUpdate(); //Forces popover to reposition. We need to do this because content has loaded after poper has already positioned itself                  
                  if (reviews.length > 1)
                     setWorkspaceUrl(reviews[0].workspaceUrl);                  
               }} />
               {!!workspaceUrl && <div className="rp-read-more">
                  <a href={`${workspaceUrl}#reviews`} target="_blank">Read more reviews</a>
               </div>}
            </PopoverBody>
         </div>)}
      </Popover>)
})

export default ReviewPopup;